/**
 * Network-risk views fronting the NetworkRisk sources.
 *
 * Five sources carry the IDENTICAL 17-timeseries flow-assurance risk-profile
 * measure set — the current-state string/pipeline sources, their per-scenario
 * variants, and the entity-grain `NetworkRisk_OptimizationScenarios` — so ONE
 * shared `RISK_PROFILE` shape fronts every grain. Only the kept grain relation
 * differs (`wellString` / `pipelineEntity` / `entityName`), plus the scenario
 * sources carry the `scenarioName` + `runID` run metadata.
 *
 * The current-state string/pipeline pair are scalar per-entity reshape views:
 * `createReshapedView` flattens the irregular per-hazard source names
 * (`asphalteneDTMax`, `corrosionRateMax`, `scaleSIRisk`, …) to clean canonical
 * hazard names; identity, the grain relation, and `network` stay verbatim.
 *
 * The three per-run scenario sources are run-indexed — the `RISK_PROFILE`
 * measures are per-row timeseries handles — so each is a timestamp-aligned
 * surface: `createAlignedSeriesView` emits one row per (source row × run
 * timestamp) with each measure as its own typed column (its unit carried as
 * column-type metadata) plus a `timestamp` column, broadcasting identity + the
 * grain relation + `network` + the `scenarioName`/`runID` run metadata onto every
 * row. Those close over the per-session CDF client, so the producer is a hook.
 */

import type { DataModelView } from '#core/lib/cognite/view.ts';

import { createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  IDENTITY,
  NETWORK_RISK_ENTITY_RELATION,
  NETWORK_RISK_RELATION,
  PIPELINE_RELATION,
  RISK_PROFILE,
  RISK_SCENARIO_META,
  type Shape,
  STRING_RELATION,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import {
  NetworkRiskOptimizationScenariosView,
  NetworkRiskPipelineAgentView,
  NetworkRiskPipelineOptimizationScenariosAgentView,
  NetworkRiskStringAgentView,
  NetworkRiskStringOptimizationScenariosAgentView,
} from '#schema/generated/ipso/views.ts';

/** One network-risk surface: its registry name, agent doc, source view, and the grain ref + scenario meta it keeps. */
type NetworkRiskSpec = {
  /** Agent-facing registry name. */
  readonly name: string;
  /** View-level description shown to the agent. */
  readonly description: string;
  /** The source CDF view this reshapes or aligns. */
  readonly source: DataModelView;
  /**
   * The grain direct-relation (plus, for scenario sources, the scenario meta)
   * kept unrenamed alongside identity + the constant `network` relation.
   */
  readonly grain: Shape;
};

/**
 * The two current-state network-risk surfaces, declared as data. Each projects
 * the shared 17-measure `RISK_PROFILE` family over one grain, keeping identity +
 * the grain relation + the constant `network` relation.
 */
const NETWORK_RISK_CURRENT_SPECS: readonly NetworkRiskSpec[] = [
  {
    name: 'NetworkRiskString',
    description:
      'Current-state flow-assurance risk per well string (WF20): aggregated risk plus per-hazard risk level and severity (asphaltene, corrosion, erosion, EVR, hydrate, PE5 stability, scale, wax). Filter by wellString (or join WellEntity) to scope to a well string.',
    source: NetworkRiskStringAgentView,
    grain: STRING_RELATION,
  },
  {
    name: 'NetworkRiskPipeline',
    description:
      'Current-state flow-assurance risk per pipeline/flowline entity (WF20): aggregated risk plus per-hazard risk level and severity (asphaltene, corrosion, erosion, EVR, hydrate, PE5 stability, scale, wax). Filter by pipelineEntity to scope to a pipeline.',
    source: NetworkRiskPipelineAgentView,
    grain: PIPELINE_RELATION,
  },
];

/**
 * The three per-run scenario network-risk surfaces, declared as data. Each
 * aligns the shared `RISK_PROFILE` family over one grain into one typed column
 * per hazard measure plus a `timestamp` column, keeping identity + the grain
 * relation + the constant `network` relation + the `scenarioName`/`runID` run
 * metadata.
 */
const NETWORK_RISK_SCENARIO_SPECS: readonly NetworkRiskSpec[] = [
  {
    name: 'NetworkRiskStringScenarios',
    description:
      'Per-scenario flow-assurance risk per well string (WF11/WF20): one row per (string × scenario × run timestamp) with each hazard measure as its own typed column. Filter by wellString and scenarioName to scope to a well string scenario.',
    source: NetworkRiskStringOptimizationScenariosAgentView,
    grain: [...STRING_RELATION, ...RISK_SCENARIO_META],
  },
  {
    name: 'NetworkRiskPipelineScenarios',
    description:
      'Per-scenario flow-assurance risk per pipeline/flowline entity (WF11/WF20): one row per (pipeline × scenario × run timestamp) with each hazard measure as its own typed column. Filter by pipelineEntity and scenarioName to scope to a pipeline scenario.',
    source: NetworkRiskPipelineOptimizationScenariosAgentView,
    grain: [...PIPELINE_RELATION, ...RISK_SCENARIO_META],
  },
  {
    name: 'NetworkRiskScenarios',
    description:
      'Per-scenario flow-assurance risk per entity (well, field, string, or facility) (WF11/WF20): one row per (entity × scenario × run timestamp) with each hazard measure as its own typed column. Filter by entityName and scenarioName to scope to an entity scenario.',
    source: NetworkRiskOptimizationScenariosView,
    grain: [...NETWORK_RISK_ENTITY_RELATION, ...RISK_SCENARIO_META],
  },
];

/**
 * Build every network-risk surface: the two current-state per-entity reshape
 * views plus the three per-run scenario aligned-series surfaces (one typed
 * column per RISK_PROFILE measure with its unit, a `timestamp` column, closing
 * over the per-session CDF client). Each keeps identity + the grain relation +
 * the constant `network` relation (and scenario meta on the scenario surfaces).
 */
export const useNetworkRiskViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  const currentViews = NETWORK_RISK_CURRENT_SPECS.map((spec: NetworkRiskSpec) =>
    createReshapedView({
      name: spec.name,
      description: spec.description,
      source: spec.source,
      keep: [...IDENTITY, ...spec.grain, ...NETWORK_RISK_RELATION],
      groups: [{ shape: RISK_PROFILE }],
      client,
      mintSeries,
    }),
  );
  const scenarioViews = NETWORK_RISK_SCENARIO_SPECS.map((spec: NetworkRiskSpec) =>
    createAlignedSeriesView({
      name: spec.name,
      description: spec.description,
      source: spec.source,
      keep: [...IDENTITY, ...spec.grain, ...NETWORK_RISK_RELATION],
      groups: { shape: RISK_PROFILE },
      client,
      mintSeries,
    }),
  );
  return [...currentViews, ...scenarioViews];
};
